import translate from 'Components/Helpers/translate';

type CkElement = {
	$: HTMLImageElement;
	is: (tag: string) => boolean;
	isReadOnly: () => boolean;
	setAttribute: (name: string, value: string) => void;
	removeAttribute: (name: string) => void;
	removeStyle: (name: string) => void;
};

type CkEditable = {
	attachListener: (
		target: CkEditable,
		event: 'mouseover' | 'mouseout',
		listener: (evt: { data: { getTarget: () => CkElement } }) => void
	) => void;
};

type CkEditor = {
	fire: (event: string) => void;
	on: {
		(event: 'doubleclick', listener: (evt: { data: { element: CkElement; dialog?: string } }) => void): void;
		(event: 'contentDom' | 'destroy', listener: () => void): void;
	};
	editable: () => CkEditable;
	window: { $: Window };
};

type CkDialog = {
	getValueOf: (page: string, field: string) => string;
	setValueOf: (page: string, field: string, value: string) => void;
};

type CkGlobal = {
	plugins: {
		add: (name: string, plugin: { requires: string; init: (editor: CkEditor) => void }) => void;
	};
	dialog: {
		exists: (name: string) => boolean;
		add: (name: string, definition: (editor: CkEditor) => object) => void;
	};
};

const CKEDITOR = window.CKEDITOR as unknown as CkGlobal;
const clicked = new WeakMap<CkEditor, CkElement>();

const imageWidthDialog = (editor: CkEditor) => {
	let image: CkElement | undefined;
	return {
		title: translate('default.image'),
		minWidth: 220,
		minHeight: 40,
		contents: [
			{
				id: 'main',
				elements: [{ type: 'text', id: 'width', label: translate('editor.imageWidth') }]
			}
		],
		onShow(this: CkDialog) {
			image = clicked.get(editor);
			this.setValueOf('main', 'width', String(image?.$.width ?? ''));
		},
		onOk(this: CkDialog) {
			const width = parseInt(this.getValueOf('main', 'width'), 10);
			if (!image || !(width > 0)) {
				return false;
			}
			editor.fire('saveSnapshot');
			// Outlook ignores a CSS width, so the size has to be the attribute.
			image.setAttribute('width', String(width));
			image.removeAttribute('height');
			image.removeStyle('width');
			image.removeStyle('height');
			editor.fire('saveSnapshot');
			editor.fire('change');
		}
	};
};

// A native title tooltip does not reliably show inside the editor's iframe, so the hint is drawn in the
// page around it instead, which also keeps it out of the saved signature.
const createTooltip = () => {
	const tooltip = document.createElement('div');
	tooltip.textContent = translate('editor.imageWidthTooltip');
	tooltip.className = 'ImageWidthTooltip';
	Object.assign(tooltip.style, {
		display: 'none',
		position: 'fixed',
		zIndex: '100000',
		pointerEvents: 'none',
		transform: 'translate(-50%, calc(-100% - 6px))',
		padding: '2px 6px',
		borderRadius: '4px',
		background: 'rgba(29, 61, 72, 0.6)',
		color: '#ffffff',
		fontSize: '11px',
		lineHeight: '16px',
		whiteSpace: 'nowrap'
	});
	document.body.appendChild(tooltip);
	return tooltip;
};

const showTooltip = (tooltip: HTMLDivElement, editor: CkEditor, image: CkElement) => {
	const frame = editor.window.$.frameElement;
	const offset = frame ? frame.getBoundingClientRect() : { top: 0, left: 0 };
	const box = image.$.getBoundingClientRect();
	tooltip.style.left = `${offset.left + box.left + box.width / 2}px`;
	tooltip.style.top = `${offset.top + box.top}px`;
	tooltip.style.display = 'block';
};

CKEDITOR.plugins.add('imagewidth', {
	requires: 'dialog',
	init: editor => {
		if (!CKEDITOR.dialog.exists('imagewidth')) {
			CKEDITOR.dialog.add('imagewidth', imageWidthDialog);
		}
		const tooltip = createTooltip();
		const hideTooltip = () => (tooltip.style.display = 'none');
		editor.on('destroy', () => tooltip.remove());
		editor.on('contentDom', () => {
			const editable = editor.editable();
			editable.attachListener(editable, 'mouseover', evt => {
				const target = evt.data.getTarget();
				if (target.is('img')) {
					showTooltip(tooltip, editor, target);
				}
			});
			editable.attachListener(editable, 'mouseout', evt => {
				if (evt.data.getTarget().is('img')) {
					hideTooltip();
				}
			});
		});
		editor.on('doubleclick', evt => {
			hideTooltip();
			const element = evt.data.element;
			if (element.is('img') && !element.isReadOnly()) {
				clicked.set(editor, element);
				evt.data.dialog = 'imagewidth';
			}
		});
	}
});
